import { Container, Plugins } from '@getgo/container-client'
import { getAvatarData } from '../avatar-helpers'
import type { ShellApi, ShellAuthContext } from '../models'
import { performanceAsyncWrapper } from '../../common/performance-decorator'
import { getShellLogger } from '../../common/logger'
import type { GoToLicense } from '../../services/feature-flags'
import type { UserAdminInfo, UserInfo } from '../../services/identity/models'

import { getUserContextPermissions } from '../../services/identity/permissions'
import { getEmailsFromUserProfile, getIdFromUserProfile } from '../../services/identity/userProfile'
import { fetchUserInfoFromInternalAPIs } from '../identity-helpers'
import { setupContextApi } from './context-api'
import { convertUserInfoToUserAdminInfo, convertUserInfoToUserProfileInfo } from '../../services/identity/helpers'
import { ShellUserInfoImpl } from '../user-info/user-info'
import { isInternal } from '../../services/feature-flags/utils'

export const getAuthContextUserProfileId = (authContext: Pick<ShellAuthContext, 'userProfile'>): string | undefined =>
  authContext.userProfile?.id

export const getAuthContextAccountKey = (
  authContext: Pick<ShellAuthContext, 'meFromExternalAdmin'>,
): string | undefined => authContext.meFromExternalAdmin?.accountKey

export const getAuthContextAccountKeys = (authContext: Pick<ShellAuthContext, 'accountKeys'>): readonly string[] =>
  authContext.accountKeys ?? []

export const getAuthContextUserProfileEntitlements = (
  authContext: Pick<ShellAuthContext, 'userProfile'>,
): readonly string[] => authContext.userProfile?.entitlements ?? []

export const getAuthContextPbxId = (authContext: Pick<ShellAuthContext, 'pbxId'>): string => authContext.pbxId ?? ''

export const getAuthContextIsCalendarConnected = (
  authContext: Pick<ShellAuthContext, 'isCalendarConnected'>,
): Promise<boolean> => authContext.isCalendarConnected ?? Promise.resolve(false)

export const getAuthContextLicenses = (authContext: Pick<ShellAuthContext, 'licenses'>): readonly GoToLicense[] =>
  authContext.licenses ?? []

export const getAuthContextProductEnabledDates = (authContext: Pick<ShellAuthContext, 'productEnabledDates'>) =>
  authContext.productEnabledDates ?? []

export const getAuthContextIsUserInternal = (authContext: Pick<ShellAuthContext, 'user'>): boolean =>
  !!authContext.user?.emails?.some(email => isInternal(email.value))

export const getAuthContextUserTimezone = (authContext: Pick<ShellAuthContext, 'user'>): string | undefined =>
  authContext.user.location.timeZone

export const getAuthContextUserCreationDate = (authContext: Pick<ShellAuthContext, 'user'>): string | undefined =>
  authContext.user.meta.createdDate?.toISOString()

const fetchFromInternalAPIs = async (): Promise<UserInfo | undefined> =>
  await performanceAsyncWrapper('internalApisFetches', async () => await fetchUserInfoFromInternalAPIs())()

const getAllAccountsKeys = (userAdminInfo: UserAdminInfo): readonly string[] =>
  userAdminInfo.accounts?.map(account => account?.key)

const getAllLicenses = (userAdminInfo: UserAdminInfo): readonly GoToLicense[] =>
  userAdminInfo.accounts
    ?.map(account => (account.licenses ?? []).map(license => ({ ...license, accountKey: account?.key })))
    ?.filter(licenses => licenses.length > 0)
    ?.flat()

/**
 * Create a ShellAuthContext with the available ShellApi
 * This is used on the children to avoid refetching all the APIs
 * @param shell
 * @returns
 */
export const getAuthContextFromShellApi = (shell?: ShellApi): ShellAuthContext => {
  const authContext: ShellAuthContext = {} as ShellAuthContext
  if (!shell) {
    return authContext
  }

  const { identity, context: contextApi, user } = shell

  authContext.user = user
  authContext.me = identity?.me
  authContext.meFromExternalAdmin = identity?.meFromExternalAdmin

  const entitlements = user.entitlements ?? []
  const emails = getEmailsFromUserProfile(user.emails)
  const firstEmail = emails[0]?.value
  const name = firstEmail
  const id = getIdFromUserProfile(user.key)

  authContext.userProfile = {
    entitlements,
    emails,
    firstEmail,
    name,
    id,
  }

  const pbxId = contextApi?.pbx?.id ?? ''
  authContext.contextApi = contextApi
  authContext.pbxId = pbxId

  authContext.permissions = identity?.permissions
  authContext.avatar = identity?.avatar
  authContext.accountKeys = identity?.meFromExternalAdmin ? getAllAccountsKeys(identity.meFromExternalAdmin) : []
  authContext.licenses = identity?.meFromExternalAdmin ? getAllLicenses(identity.meFromExternalAdmin) : []
  return authContext
}

/**
 * Create a ShellAuthContext based by aggregating API.
 * This is used on the main window
 * @returns Promise<ShellAuthContext>
 */
export const getAuthContextFromApi = async (): Promise<ShellAuthContext> => {
  const authContext: ShellAuthContext = {
    user: new ShellUserInfoImpl({}),
  } as ShellAuthContext

  const userInfo = await fetchFromInternalAPIs()

  getShellLogger().setContext('shell-user-key', userInfo?.user?.key ?? 'guest')

  if (!userInfo) {
    return authContext
  }

  const userProfileInfo = convertUserInfoToUserProfileInfo(userInfo)
  authContext.me = userProfileInfo
  authContext.meFromExternalAdmin = convertUserInfoToUserAdminInfo(userInfo)

  const entitlements = userProfileInfo?.entitlements ?? []
  const emails = getEmailsFromUserProfile(userProfileInfo?.emails)
  const firstEmail = emails[0]?.value
  const name = firstEmail
  const id = getIdFromUserProfile(userInfo.user?.key)

  authContext.userProfile = {
    entitlements,
    emails,
    firstEmail,
    name,
    id,
  }

  // This could go elsewhere
  if (id && Container.isPluginAvailable('UserId')) {
    Plugins.UserId.storeCurrAuthenticatedUserKey(id)
  }

  /**
   * Warn if no entitlements are available.
   * This should not be possible but some users data are missing entitlements.
   */
  if (entitlements.length === 0) {
    getShellLogger().warn('User does not have entitlements.')
  }

  const contextApi = await setupContextApi(firstEmail, id)
  const pbxId = contextApi?.pbx?.id ?? ''
  authContext.contextApi = contextApi
  authContext.pbxId = pbxId

  const [userContextResponse, avatar] = await Promise.all([
    getUserContextPermissions(pbxId),
    getAvatarData(authContext.user),
  ])

  const permissions = userContextResponse.permissions ?? []
  const userContextLicense = userContextResponse.license

  authContext.user = new ShellUserInfoImpl({
    userInfo,
    permissions,
    avatar,
    licenses: userContextLicense ? [userContextLicense] : [],
  })

  authContext.permissions = permissions
  authContext.avatar = avatar
  authContext.accountKeys = authContext.meFromExternalAdmin ? getAllAccountsKeys(authContext.meFromExternalAdmin) : []
  authContext.licenses = authContext.meFromExternalAdmin ? getAllLicenses(authContext.meFromExternalAdmin) : []
  return authContext
}
